import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment-timezone";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Loader from "../layout/Loader";
import PermissionGate from "../layout/PermissionGate";
import BillingAddress from "../layout/BillingAddress";
import normalize from "../utils/NormalizeSelect";

const MERCHANT_TYPES = ["Regular", "Ecommerce", "API User", "Customer"];
const CURRENCIES = ["KWD", "JOD", "SAR", "AED", "BHD", "QAR", "EGP"];

const addressLine = (part) => {
    if (!part) {
        return "---";
    }
    if (typeof part === "string") {
        return part;
    }
    return part.label || part.value || "---";
};

const MerchantSignupDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [models, setModels] = useState([]);
    const [pricingModel, setPricingModel] = useState(null);
    const [rejectionReason, setRejectionReason] = useState("");
    const [popup, setPopup] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState({});
    const [editErrors, setEditErrors] = useState({});
    const [savingEdit, setSavingEdit] = useState(false);

    const fetchSignup = () => {
        axios
            .get(
                `${process.env.REACT_APP_API_ENDPOINT}merchant-signup-details?id=${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            )
            .then((res) => {
                if (res.data.code === 1) {
                    setData(res.data.data);
                    setIsLoading(false);
                } else {
                    setIsLoading(false);
                }
            })
            .catch(console.error);
    };

    const fetchPricingModels = () => {
        axios
            .get(`${process.env.REACT_APP_API_ENDPOINT}pricing/list`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((res) => {
                if (res.data.code === 1) {
                    setModels(res.data.data);
                }
            })
            .catch(console.error);
    };

    useEffect(() => {
        fetchSignup();
        fetchPricingModels();
        // eslint-disable-next-line
    }, [id]);

    useEffect(() => {
        if (data?.requestedPricingModel && models.length > 0) {
            const match = models.find(
                (model) => model.value === data.requestedPricingModel,
            );
            if (match) {
                setPricingModel(match);
            }
        }
    }, [data, models]);

    const approve = () => {
        if (!pricingModel) {
            setError("Please select a pricing model before approving.");
            return;
        }
        setSubmitting(true);
        axios
            .post(
                `${process.env.REACT_APP_API_ENDPOINT}approve-merchant-signup`,
                {
                    id: data._id,
                    pricingModel: pricingModel.value,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            )
            .then((res) => {
                setSubmitting(false);
                if (res.data.code === 1) {
                    setPopup("");
                    navigate(`/merchants/merchant-details/${res.data.data.merchantId}`);
                } else {
                    setError(res.data.message);
                    setPopup("");
                }
            })
            .catch(() => {
                setSubmitting(false);
                setError("Something went wrong. Please try again.");
                setPopup("");
            });
    };

    const reject = () => {
        setSubmitting(true);
        axios
            .post(
                `${process.env.REACT_APP_API_ENDPOINT}reject-merchant-signup`,
                {
                    id: data._id,
                    reason: rejectionReason,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            )
            .then((res) => {
                setSubmitting(false);
                setPopup("");
                if (res.data.code === 1) {
                    fetchSignup();
                } else {
                    setError(res.data.message);
                }
            })
            .catch(() => {
                setSubmitting(false);
                setPopup("");
                setError("Something went wrong. Please try again.");
            });
    };

    const startEdit = () => {
        setEditData({
            merchantName: data.merchantName,
            legalName: data.legalName,
            merchantType: data.merchantType,
            currency: data.currency,
            licenceExpiary: data.licenceExpiary,
            mainBranchPhone: data.mainBranchPhone,
            email: data.email,
            billingArea: data.billingArea,
            billingBlock: data.billingBlock,
            billingStreet: data.billingStreet,
            billingBuildingNoOrName: data.billingBuildingNoOrName,
            billingJadda: data.billingJadda,
            billingApartmentOrOffice: data.billingApartmentOrOffice,
            billingFloor: data.billingFloor,
            billingLatitude: data.billingLatitude,
            billingLongitude: data.billingLongitude,
            billingAndLegalAddressSame: true,
        });
        setEditErrors({});
        setEditMode(true);
    };

    const cancelEdit = () => {
        setEditMode(false);
        setEditData({});
        setEditErrors({});
    };

    const editProcessData = (type, event, field) => {
        if (type === "text") {
            const value = event.target.value;
            setEditData((d) => ({ ...d, [field]: value }));
            setEditErrors((e) => ({ ...e, [field]: undefined }));
        }
        if (type === "select") {
            const value = event?.value;
            setEditData((d) => ({ ...d, [field]: value }));
            setEditErrors((e) => ({ ...e, [field]: undefined }));
        }
        if (type === "date") {
            const value = moment(event).format("DD-MM-YYYY");
            setEditData((d) => ({ ...d, [field]: value }));
            setEditErrors((e) => ({ ...e, [field]: undefined }));
        }
    };

    const saveEdit = () => {
        setSavingEdit(true);
        axios
            .post(
                `${process.env.REACT_APP_API_ENDPOINT}update-merchant-signup`,
                { id: data._id, ...editData },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            )
            .then((res) => {
                setSavingEdit(false);
                if (res.data.code === 1) {
                    setData(res.data.data);
                    setEditMode(false);
                    setEditErrors({});
                    setError("");
                } else {
                    if (res.data.data?.field) {
                        setEditErrors({ [res.data.data.field]: res.data.message });
                    }
                    setError(res.data.message);
                }
            })
            .catch(() => {
                setSavingEdit(false);
                setError("Something went wrong. Please try again.");
            });
    };

    const statusBadgeClass = (value) => {
        if (value === "approved") {
            return "badge text-white bg-success";
        }
        if (value === "rejected") {
            return "badge text-white bg-danger";
        }
        return "badge text-white bg-warning";
    };

    const documents = data
        ? [
            { key: "licensePhoto", label: "Trade License" },
            { key: "contractPhoto", label: "Contract Paper" },
            { key: "merchantLogo", label: "Merchant Logo" },
            { key: "signatoryDocument", label: "Signatory Document" },
            { key: "articlesOfAssociation", label: "Articles of Association" },
        ].filter(
            (doc) => data[doc.key] && data[doc.key] !== "pending-upload",
        )
        : [];

    return isLoading === true || data === null ? (
        <Loader />
    ) : (
        <div className="d-flex flex-column flex-root">
            <div className="page d-flex flex-row flex-column-fluid">
                <div
                    className="wrapper d-flex flex-column flex-row-fluid"
                    id="kt_wrapper"
                >
                    <Header />
                    <div className="app-body">
                        <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
                            <div
                                id="kt_toolbar_container"
                                className=" container-xxl  d-flex flex-stack flex-wrap gap-2"
                            >
                                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                                    <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                                        {data.merchantName}
                                    </h1>
                                    <span className={statusBadgeClass(data.status)}>
                                        {data.status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div
                            id="kt_content_container"
                            className="d-flex flex-column-fluid align-items-start  container-xxl "
                        >
                            <div className="content flex-row-fluid" id="kt_content">
                                <PermissionGate required={`view_merchant_signups`}>
                                    {error !== "" ? (
                                        <div className="alert alert-danger">{error}</div>
                                    ) : (
                                        ""
                                    )}

                                    {data.status === "rejected" ? (
                                        <div className="alert alert-danger">
                                            <b>Rejected</b>
                                            {data.reviewedAt
                                                ? ` on ${moment(data.reviewedAt)
                                                    .tz(process.env.REACT_APP_TIMEZONE)
                                                    .format("Do MMM, YYYY")}`
                                                : ""}
                                            {data.rejectionReason ? `: ${data.rejectionReason}` : ""}
                                            <div className="mt-1">
                                                You can still edit the details and approve this
                                                application if you'd like to reconsider it.
                                            </div>
                                        </div>
                                    ) : (
                                        ""
                                    )}

                                    <div className="card mb-6">
                                        <div className="card-header">
                                            <h3 className="card-title">Applicant Details</h3>
                                            {(data.status === "pending" || data.status === "rejected") && !editMode ? (
                                                <PermissionGate
                                                    required={`approve_merchant_signup`}
                                                    fallback={<></>}
                                                >
                                                    <div className="card-toolbar">
                                                        <button
                                                            onClick={startEdit}
                                                            className="btn btn-sm btn-light-primary"
                                                        >
                                                            <i className="bi-duotone bi-pencil fs-5 me-1"></i>
                                                            Edit
                                                        </button>
                                                    </div>
                                                </PermissionGate>
                                            ) : (
                                                ""
                                            )}
                                        </div>
                                        <div className="card-body">
                                            {editMode ? (
                                                <div className="row">
                                                    <div className="col-md-4 mb-5">
                                                        <label className="form-label required">
                                                            Merchant Name
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editData.merchantName ?? ""}
                                                            onChange={(e) =>
                                                                editProcessData("text", e, "merchantName")
                                                            }
                                                        />
                                                        {editErrors.merchantName ? (
                                                            <small className="text-danger mt-2">
                                                                {editErrors.merchantName}
                                                            </small>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <label className="form-label required">
                                                            Legal Name
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editData.legalName ?? ""}
                                                            onChange={(e) =>
                                                                editProcessData("text", e, "legalName")
                                                            }
                                                        />
                                                        {editErrors.legalName ? (
                                                            <small className="text-danger mt-2">
                                                                {editErrors.legalName}
                                                            </small>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <label className="form-label required">
                                                            Merchant Type
                                                        </label>
                                                        <Select
                                                            className="react-select-styled react-select-bordered"
                                                            classNamePrefix="react-select"
                                                            value={
                                                                editData.merchantType
                                                                    ? { value: editData.merchantType, label: editData.merchantType }
                                                                    : null
                                                            }
                                                            onChange={(e) =>
                                                                setEditData((d) => ({
                                                                    ...d,
                                                                    merchantType: e?.value,
                                                                }))
                                                            }
                                                            options={MERCHANT_TYPES.map((type) => ({
                                                                value: type,
                                                                label: type,
                                                            }))}
                                                            placeholder="Select an option"
                                                        />
                                                        {editErrors.merchantType ? (
                                                            <small className="text-danger mt-2">
                                                                {editErrors.merchantType}
                                                            </small>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <label className="form-label required">
                                                            Currency
                                                        </label>
                                                        <Select
                                                            className="react-select-styled react-select-bordered"
                                                            classNamePrefix="react-select"
                                                            value={
                                                                editData.currency
                                                                    ? { value: editData.currency, label: editData.currency }
                                                                    : null
                                                            }
                                                            onChange={(e) =>
                                                                setEditData((d) => ({
                                                                    ...d,
                                                                    currency: e?.value,
                                                                }))
                                                            }
                                                            options={CURRENCIES.map((currency) => ({
                                                                value: currency,
                                                                label: currency,
                                                            }))}
                                                            placeholder="Select an option"
                                                        />
                                                        {editErrors.currency ? (
                                                            <small className="text-danger mt-2">
                                                                {editErrors.currency}
                                                            </small>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <label className="form-label required">
                                                            License Expiry
                                                        </label>
                                                        <Flatpickr
                                                            options={{ dateFormat: "d-m-Y" }}
                                                            value={editData.licenceExpiary || ""}
                                                            onChange={([date]) =>
                                                                editProcessData("date", date, "licenceExpiary")
                                                            }
                                                            className="form-control"
                                                            placeholder="Pick date"
                                                        />
                                                        {editErrors.licenceExpiary ? (
                                                            <small className="text-danger mt-2">
                                                                {editErrors.licenceExpiary}
                                                            </small>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <label className="form-label required">
                                                            Email
                                                        </label>
                                                        <input
                                                            type="email"
                                                            className="form-control"
                                                            value={editData.email ?? ""}
                                                            onChange={(e) =>
                                                                editProcessData("text", e, "email")
                                                            }
                                                        />
                                                        <small className="text-muted mt-2 d-block">
                                                            This is also their login username — the
                                                            applicant will need the corrected address to
                                                            sign in.
                                                        </small>
                                                        {editErrors.email ? (
                                                            <small className="text-danger mt-2">
                                                                {editErrors.email}
                                                            </small>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <label className="form-label required">
                                                            Phone
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editData.mainBranchPhone ?? ""}
                                                            onChange={(e) =>
                                                                editProcessData(
                                                                    "text",
                                                                    e,
                                                                    "mainBranchPhone",
                                                                )
                                                            }
                                                            placeholder="9XXXXXXX"
                                                        />
                                                        {editErrors.mainBranchPhone ? (
                                                            <small className="text-danger mt-2">
                                                                {editErrors.mainBranchPhone}
                                                            </small>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="row">
                                                    <div className="col-md-4 mb-5">
                                                        <div className="text-muted fs-7">Merchant Name</div>
                                                        <div className="fw-bold">{data.merchantName}</div>
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <div className="text-muted fs-7">Legal Name</div>
                                                        <div className="fw-bold">{data.legalName}</div>
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <div className="text-muted fs-7">Merchant Type</div>
                                                        <div className="fw-bold">{data.merchantType}</div>
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <div className="text-muted fs-7">Currency</div>
                                                        <div className="fw-bold">{data.currency}</div>
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <div className="text-muted fs-7">
                                                            License Expiry
                                                        </div>
                                                        <div className="fw-bold">
                                                            {data.licenceExpiary}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <div className="text-muted fs-7">Email</div>
                                                        <div className="fw-bold">{data.email}</div>
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <div className="text-muted fs-7">Phone</div>
                                                        <div className="fw-bold">
                                                            {data.mainBranchPhone}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <div className="text-muted fs-7">Applied On</div>
                                                        <div className="fw-bold">
                                                            {moment(data.createdAt)
                                                                .tz(process.env.REACT_APP_TIMEZONE)
                                                                .format("Do MMM, YYYY, h:mm A")}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4 mb-5">
                                                        <div className="text-muted fs-7">Referral Source</div>
                                                        <div className="fw-bold">
                                                            {data.referralSource ? (
                                                                <span className="badge bg-light-info text-info fw-bold">
                                                                    {data.referralSource}
                                                                </span>
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="card mb-6">
                                        {editMode ? (
                                            <BillingAddress
                                                data={editData}
                                                processData={editProcessData}
                                                error={editErrors}
                                                setData={setEditData}
                                                setError={setEditErrors}
                                                column={2}
                                            />
                                        ) : (
                                            <>
                                                <div className="card-header">
                                                    <h3 className="card-title">Address</h3>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        <div className="col-md-3 mb-5">
                                                            <div className="text-muted fs-7">Area</div>
                                                            <div className="fw-bold">
                                                                {addressLine(data.billingArea)}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3 mb-5">
                                                            <div className="text-muted fs-7">Block</div>
                                                            <div className="fw-bold">
                                                                {addressLine(data.billingBlock)}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3 mb-5">
                                                            <div className="text-muted fs-7">Street</div>
                                                            <div className="fw-bold">
                                                                {addressLine(data.billingStreet)}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3 mb-5">
                                                            <div className="text-muted fs-7">Building</div>
                                                            <div className="fw-bold">
                                                                {data.billingBuildingNoOrName}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3 mb-5">
                                                            <div className="text-muted fs-7">Floor</div>
                                                            <div className="fw-bold">{data.billingFloor}</div>
                                                        </div>
                                                        <div className="col-md-3 mb-5">
                                                            <div className="text-muted fs-7">
                                                                Apartment / Office
                                                            </div>
                                                            <div className="fw-bold">
                                                                {data.billingApartmentOrOffice}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3 mb-5">
                                                            <div className="text-muted fs-7">Jadda</div>
                                                            <div className="fw-bold">{data.billingJadda}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div className="card mb-6">
                                        <div className="card-header">
                                            <h3 className="card-title">Documents</h3>
                                        </div>
                                        <div className="card-body">
                                            {documents.length === 0 ? (
                                                <div className="text-muted">
                                                    No documents uploaded.
                                                </div>
                                            ) : (
                                                <div className="d-flex flex-wrap gap-5">
                                                    {documents.map((doc) => (
                                                        <a
                                                            key={doc.key}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            href={
                                                                process.env.REACT_APP_API_PUBLIC_URL +
                                                                data[doc.key]
                                                            }
                                                            className="btn btn-sm btn-light-primary"
                                                            download
                                                        >
                                                            <i className="bi-duotone bi-file-earmark-text fs-5 me-1"></i>
                                                            {doc.label}
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {editMode ? (
                                        <div className="card mb-6">
                                            <div className="card-body d-flex gap-3">
                                                <button
                                                    onClick={saveEdit}
                                                    className="btn btn-primary"
                                                    disabled={savingEdit}
                                                >
                                                    {savingEdit ? "Saving..." : "Save changes"}
                                                </button>
                                                <button
                                                    onClick={cancelEdit}
                                                    className="btn btn-light"
                                                    disabled={savingEdit}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        ""
                                    )}

                                    {(data.status === "pending" || data.status === "rejected") && !editMode ? (
                                        <PermissionGate
                                            required={`approve_merchant_signup`}
                                            fallback={<></>}
                                        >
                                            <div className="card mb-6">
                                                <div className="card-header">
                                                    <h3 className="card-title">Review</h3>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        <div className="col-md-6 mb-5">
                                                            <label className="form-label required">
                                                                Pricing Model
                                                            </label>
                                                            <Select
                                                                className="react-select-styled react-select-bordered"
                                                                classNamePrefix="react-select"
                                                                value={pricingModel}
                                                                onChange={(e) => {
                                                                    setPricingModel(e);
                                                                    setError("");
                                                                }}
                                                                options={models}
                                                                filterOption={(option, input) => {
                                                                    return normalize(option.label).includes(
                                                                        normalize(input),
                                                                    );
                                                                }}
                                                                placeholder="Select an option"
                                                            />
                                                            {data.requestedPricingModel ? (
                                                                <small className="text-muted mt-2 d-block">
                                                                    Applicant requested:{" "}
                                                                    {
                                                                        models.find(
                                                                            (model) =>
                                                                                model.value ===
                                                                                data.requestedPricingModel,
                                                                        )?.label
                                                                    }
                                                                </small>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-3">
                                                        <button
                                                            onClick={() => setPopup("approve")}
                                                            className="btn btn-success"
                                                        >
                                                            {data.status === "rejected"
                                                                ? "Approve anyway"
                                                                : "Approve"}
                                                        </button>
                                                        {data.status === "pending" ? (
                                                            <button
                                                                onClick={() => setPopup("reject")}
                                                                className="btn btn-danger"
                                                            >
                                                                Reject
                                                            </button>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </PermissionGate>
                                    ) : (
                                        ""
                                    )}
                                </PermissionGate>
                            </div>
                        </div>
                    </div>

                    {/* start :: Approve */}
                    <div
                        className={
                            popup === "approve" ? "modal fade show d-block" : "modal fade"
                        }
                        tabIndex={-1}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-sm">
                            <div className="modal-content text-center bg-success text-light">
                                <div className="modal-header">
                                    <h5 className="modal-title mx-auto text-light">
                                        APPROVE APPLICATION
                                    </h5>
                                </div>
                                <div className="modal-body">
                                    <p className="h4 text-light">
                                        Approve {data.merchantName} and create their merchant
                                        account?
                                    </p>
                                </div>
                                <div className="modal-footer justify-content-center">
                                    <button
                                        onClick={() => setPopup("")}
                                        type="button"
                                        className="btn btn-light btn-sm"
                                        disabled={submitting}
                                    >
                                        No
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm"
                                        onClick={approve}
                                        disabled={submitting}
                                    >
                                        Yes
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* end :: Approve */}

                    {/* start :: Reject */}
                    <div
                        className={
                            popup === "reject" ? "modal fade show d-block" : "modal fade"
                        }
                        tabIndex={-1}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content text-center bg-danger text-light">
                                <div className="modal-header">
                                    <h5 className="modal-title mx-auto text-light">
                                        REJECT APPLICATION
                                    </h5>
                                </div>
                                <div className="modal-body">
                                    <p className="h5 text-light mb-3">
                                        Reject {data.merchantName}'s application?
                                    </p>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        placeholder="Reason (optional, included in the applicant's email)"
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                    />
                                </div>
                                <div className="modal-footer justify-content-center">
                                    <button
                                        onClick={() => setPopup("")}
                                        type="button"
                                        className="btn btn-light btn-sm"
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm"
                                        onClick={reject}
                                        disabled={submitting}
                                    >
                                        Reject
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* end :: Reject */}

                    <Footer />
                </div>
            </div>
        </div>
    );
};

export default MerchantSignupDetails;